import { Modal, Button, TextArea, Chip, Input } from '@heroui/react'
import { useState, useEffect } from 'react'
import { useUpdateFavoriteNote } from '../hooks/use-update-favorite-note'
import { useTranslations } from 'use-intl'
import { X } from 'lucide-react'

interface PrivateNoteModalProps {
  isOpen: boolean
  onClose: () => void
  shopId: number
  initialNote?: string | null
  initialTags?: string[] | null
}

export function PrivateNoteModal({ isOpen, onClose, shopId, initialNote, initialTags }: Readonly<PrivateNoteModalProps>) {
  const [note, setNote] = useState(initialNote || '')
  const [tags, setTags] = useState<string[]>(initialTags || [])
  const [tagInput, setTagInput] = useState('')
  const updateNoteMutation = useUpdateFavoriteNote()
  const t = useTranslations('discovery')

  useEffect(() => {
    if (isOpen) {
      setNote(initialNote || '')
      setTags(initialTags || [])
      setTagInput('')
    }
  }, [isOpen, initialNote, initialTags])

  const handleAddTag = () => {
    const trimmed = tagInput.trim()
    if (trimmed && tags.length < 5 && !tags.includes(trimmed)) {
      setTags([...tags, trimmed])
      setTagInput('')
    }
  }

  const handleRemoveTag = (tag: string) => {
    setTags(tags.filter(t => t !== tag))
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleAddTag()
    }
  }

  const handleSave = () => {
    updateNoteMutation.mutate({ shopId, note, tags }, {
      onSuccess: () => {
        onClose()
      },
    })
  }

  return (
    <Modal.Backdrop isOpen={isOpen} onOpenChange={(open) => !open && onClose()} variant="blur">
      <Modal.Container>
        <Modal.Dialog aria-describedby="private-note-modal-description">
          <Modal.CloseTrigger />
          <Modal.Header>
            <Modal.Heading>{t('privateNoteModal.title', { defaultValue: 'Add Private Note' })}</Modal.Heading>
          </Modal.Header>
          <Modal.Body>
            <p id="private-note-modal-description" className="sr-only">
              {t('privateNoteModal.description', { defaultValue: 'Write a private note and tags for this restaurant.' })}
            </p>
            <TextArea
              placeholder={t('privateNoteModal.placeholder', { defaultValue: 'Write a private note about this shop...' })}
              value={note}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setNote(e.target.value)}
              maxLength={500}
              rows={4}
              className="w-full"
            />
            <div className="text-xs text-stone-500 mt-1">{note.length}/500 {t('privateNoteModal.characters', { defaultValue: 'characters' })}</div>

            {/* Tags Section */}
            <div className="mt-4">
              <div className="flex flex-wrap gap-1.5 mb-2">
                {tags.map(tag => (
                  <Chip
                    key={tag}
                    variant="soft"
                    size="sm"
                  >
                    <span className="flex items-center gap-1">
                      {tag}
                      <button
                        type="button"
                        onClick={() => handleRemoveTag(tag)}
                        className="p-1 -mr-0.5 rounded-full hover:bg-stone-200 dark:hover:bg-stone-700 transition-colors"
                        aria-label={`Remove tag ${tag}`}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  </Chip>
                ))}
              </div>
              {tags.length < 5 && (
                <Input
                  className="text-sm"
                  placeholder={t('privateNoteModal.tagPlaceholder', { defaultValue: 'Add tag (press Enter)' })}
                  value={tagInput}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTagInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  maxLength={50}
                />
              )}
              <div className="text-xs text-stone-500 mt-1">{tags.length}/5 {t('privateNoteModal.tags', { defaultValue: 'tags' })}</div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="ghost" onPress={onClose}>
              {t('privateNoteModal.cancel', { defaultValue: 'Cancel' })}
            </Button>
            <Button
              className="bg-primary text-white"
              onPress={handleSave}
              isDisabled={updateNoteMutation.isPending}
            >
              {t('privateNoteModal.save', { defaultValue: 'Save Note' })}
            </Button>
          </Modal.Footer>
        </Modal.Dialog>
      </Modal.Container>
    </Modal.Backdrop>
  )
}
